// End-to-end encryption between this browser and a practice computer for the
// live view. Rudi relays the messages and never holds a key.
//
// The browser makes an ephemeral ECDH P-256 key pair per session. The practice
// computer answers with its own ephemeral P-256 key, signed with its Ed25519
// device key under the context `rudi-practice-live-v1`. Both sides derive one
// AES-256-GCM key with HKDF-SHA256 (salt: the session ID, info: the context).
// Every data message has a random 12-byte IV and the AAD
// `<sessionId>\n<seq>\n<direction>`, and each side numbers its own data
// messages from 1. Binary values travel as base64url without padding.

export const LIVE_CONTEXT = 'rudi-practice-live-v1';

/** `b2d` from browser to device, `d2b` from device to browser. */
export type LiveDirection = 'b2d' | 'd2b';

export type LiveCryptoFailure =
  'unsupported' | 'key' | 'signature' | 'decrypt' | 'repeat' | 'gap' | 'encoding';

export class LiveCryptoError extends Error {
  readonly reason: LiveCryptoFailure;
  constructor(reason: LiveCryptoFailure, message: string) {
    super(message);
    this.name = 'LiveCryptoError';
    this.reason = reason;
  }
}

const P256_RAW_BYTES = 65;
const ED25519_PUBLIC_BYTES = 32;
const ED25519_SIGNATURE_BYTES = 64;
const IV_BYTES = 12;
const BASE64URL = /^[A-Za-z0-9_-]*$/;

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

function subtle(): SubtleCrypto {
  const value = globalThis.crypto?.subtle;
  if (!value)
    throw new LiveCryptoError(
      'unsupported',
      'This browser has no WebCrypto here. Open Cortex over HTTPS in a current browser.'
    );
  return value;
}

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decodes base64url without padding. Throws on padding or any other alphabet. */
export function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  if (typeof text !== 'string' || !BASE64URL.test(text) || text.length % 4 === 1)
    throw new LiveCryptoError('encoding', 'Expected base64url without padding.');
  const padded =
    text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function bytesOf(text: string, length: number, what: string): Uint8Array<ArrayBuffer> {
  const bytes = fromBase64Url(text);
  if (bytes.length !== length)
    throw new LiveCryptoError('key', `${what} must have ${length} bytes, not ${bytes.length}.`);
  return bytes;
}

/** First 16 hex characters of SHA-256 over a raw public key, as Rudi shows key IDs. */
export async function keyIdOf(publicKey: string): Promise<string> {
  const digest = new Uint8Array(await subtle().digest('SHA-256', fromBase64Url(publicKey)));
  return Array.from(digest.slice(0, 8), byte => byte.toString(16).padStart(2, '0')).join('');
}

export interface EphemeralKey {
  /** Not extractable; it lives only as long as the session object holding it. */
  privateKey: CryptoKey;
  /** The uncompressed 65-byte point, base64url. */
  publicKey: string;
}

export async function generateEphemeralKey(): Promise<EphemeralKey> {
  const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, [
    'deriveBits',
  ]);
  const raw = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
  return { privateKey: pair.privateKey, publicKey: toBase64Url(raw) };
}

/** The exact text the device key signs: context, then session and both ephemeral keys. */
export function helloSignedText(input: {
  sessionId: string;
  browserPublicKey: string;
  devicePublicKey: string;
}): string {
  return [LIVE_CONTEXT, input.sessionId, input.browserPublicKey, input.devicePublicKey].join('\n');
}

/**
 * Checks the device's hello: its ephemeral P-256 key, signed with the Ed25519
 * device key Rudi named when the session opened. Throws unless it verifies.
 */
export async function verifyHello(input: {
  sessionId: string;
  browserPublicKey: string;
  /** The device's ephemeral P-256 key from the hello. */
  devicePublicKey: string;
  signature: string;
  /** The device's Ed25519 key from the open answer. */
  deviceSigningKey: string;
}): Promise<void> {
  const publicKey = bytesOf(input.deviceSigningKey, ED25519_PUBLIC_BYTES, 'The device key');
  const signature = bytesOf(input.signature, ED25519_SIGNATURE_BYTES, 'The hello signature');
  bytesOf(input.devicePublicKey, P256_RAW_BYTES, "The device's session key");
  let key: CryptoKey;
  try {
    key = await subtle().importKey('raw', publicKey, { name: 'Ed25519' }, false, ['verify']);
  } catch (error) {
    if ((error as { name?: string })?.name === 'NotSupportedError')
      throw new LiveCryptoError(
        'unsupported',
        'This browser cannot check Ed25519 signatures, so it cannot confirm it talks to the practice computer. Use a current Chrome, Edge, Firefox or Safari.'
      );
    throw new LiveCryptoError('key', 'Rudi sent a device key that is not an Ed25519 key.');
  }
  const valid = await subtle().verify(
    { name: 'Ed25519' },
    key,
    signature,
    encoder.encode(helloSignedText(input))
  );
  if (!valid)
    throw new LiveCryptoError(
      'signature',
      "The practice computer's signature did not match its device key."
    );
}

/** ECDH, then HKDF-SHA256 with the session ID as salt, into one AES-256-GCM key. */
export async function deriveSessionKey(input: {
  privateKey: CryptoKey;
  /** The other side's ephemeral P-256 key, base64url. */
  peerPublicKey: string;
  sessionId: string;
}): Promise<CryptoKey> {
  const raw = bytesOf(input.peerPublicKey, P256_RAW_BYTES, 'A P-256 public key');
  if (raw[0] !== 4) throw new LiveCryptoError('key', 'A P-256 public key must be uncompressed.');
  let peer: CryptoKey;
  try {
    peer = await subtle().importKey('raw', raw, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  } catch {
    throw new LiveCryptoError('key', 'The P-256 public key is not a point on the curve.');
  }
  const shared = await subtle().deriveBits({ name: 'ECDH', public: peer }, input.privateKey, 256);
  const material = await subtle().importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: encoder.encode(input.sessionId),
      info: encoder.encode(LIVE_CONTEXT),
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export function liveAad(sessionId: string, seq: number, direction: LiveDirection) {
  return encoder.encode(`${sessionId}\n${seq}\n${direction}`);
}

export interface SealedMessage {
  seq: number;
  iv: string;
  /** AES-GCM output, ciphertext followed by the 16-byte tag. */
  ciphertext: string;
}

export async function encryptMessage(
  key: CryptoKey,
  input: { sessionId: string; seq: number; direction: LiveDirection; plaintext: string }
): Promise<SealedMessage> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: liveAad(input.sessionId, input.seq, input.direction) },
    key,
    encoder.encode(input.plaintext)
  );
  return { seq: input.seq, iv: toBase64Url(iv), ciphertext: toBase64Url(new Uint8Array(sealed)) };
}

export async function decryptMessage(
  key: CryptoKey,
  input: { sessionId: string; direction: LiveDirection } & SealedMessage
): Promise<string> {
  let iv: Uint8Array<ArrayBuffer>;
  let ciphertext: Uint8Array<ArrayBuffer>;
  try {
    iv = bytesOf(input.iv, IV_BYTES, 'An IV');
    ciphertext = fromBase64Url(input.ciphertext);
  } catch {
    throw new LiveCryptoError('decrypt', 'A message arrived with a malformed IV or ciphertext.');
  }
  try {
    const plain = await subtle().decrypt(
      { name: 'AES-GCM', iv, additionalData: liveAad(input.sessionId, input.seq, input.direction) },
      key,
      ciphertext
    );
    return decoder.decode(plain);
  } catch {
    throw new LiveCryptoError('decrypt', 'A message failed its integrity check.');
  }
}

/**
 * One side's view of an established session: it numbers its own messages
 * from 1 and accepts the other side's only in order, each number once.
 */
export class LiveChannel {
  private key: CryptoKey | null;
  private sent = 0;
  private received = 0;
  readonly sessionId: string;
  private readonly outgoing: LiveDirection;
  private readonly incoming: LiveDirection;

  constructor(key: CryptoKey, sessionId: string, outgoing: LiveDirection = 'b2d') {
    this.key = key;
    this.sessionId = sessionId;
    this.outgoing = outgoing;
    this.incoming = outgoing === 'b2d' ? 'd2b' : 'b2d';
  }

  /** The number of the last message accepted from the other side, 0 before any. */
  get lastReceived(): number {
    return this.received;
  }

  private current(): CryptoKey {
    if (!this.key) throw new LiveCryptoError('key', 'The live view session is closed.');
    return this.key;
  }

  async seal(plaintext: string): Promise<SealedMessage> {
    const key = this.current();
    this.sent += 1;
    return encryptMessage(key, {
      sessionId: this.sessionId,
      seq: this.sent,
      direction: this.outgoing,
      plaintext,
    });
  }

  async open(message: SealedMessage): Promise<string> {
    const key = this.current();
    const seq = message.seq;
    if (!Number.isSafeInteger(seq) || seq <= this.received)
      throw new LiveCryptoError('repeat', `Message ${seq} arrived again or out of order.`);
    if (seq !== this.received + 1)
      throw new LiveCryptoError('gap', `Message ${this.received + 1} is missing.`);
    const plaintext = await decryptMessage(key, {
      ...message,
      sessionId: this.sessionId,
      direction: this.incoming,
    });
    // Advance only once the message proved itself, so a forged one cannot skip ahead.
    this.received = seq;
    return plaintext;
  }

  /** Drops the key. Every later seal or open throws. */
  destroy(): void {
    this.key = null;
  }
}
